import React, { useState } from 'react';
import { Building, ShieldCheck, Cpu, HardDrive, Database, Settings, Plus, UserPlus, Check, X, Shield, Lock, Zap, CheckCircle2, AlertTriangle } from 'lucide-react';

export interface TenantAdmin {
  id: string;
  adminName: string;
  adminEmail: string;
  schoolName: string;
  slug: string;
  plan: string;
  modules: string[];
  studentCount: number;
  subscriptionStatus: 'ACTIVE' | 'EXPIRED' | 'PENDING_ACTIVATION';
  subscriptionExpiry: string;
}

const INITIAL_TENANT_ADMINS: TenantAdmin[] = [
  { 
    id: '1', 
    adminName: 'Dr. Robert Vance', 
    adminEmail: 'principal@stjude.edu', 
    schoolName: 'St. Jude Special School', 
    slug: 'stjude', 
    plan: 'ENTERPRISE_SCHOOL', 
    studentCount: 142,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-08-31',
    modules: ['aac', 'speech-therapy', 'autism', 'student', 'teacher', 'therapist', 'ai', 'billing'] 
  },
  { 
    id: '2', 
    adminName: 'Dr. Ananya Sharma', 
    adminEmail: 'admin@sunshinetherapy.org', 
    schoolName: 'Sunshine Therapy Centre', 
    slug: 'sunshine', 
    plan: 'PRO_CLINIC', 
    studentCount: 88,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-04-15',
    modules: ['aac', 'speech-therapy', 'therapist', 'billing', 'communication'] 
  },
  { 
    id: '3', 
    adminName: 'Superintendent K. Patil', 
    adminEmail: 'director@districtrehab.gov', 
    schoolName: 'District Rehab Department', 
    slug: 'rehab', 
    plan: 'GOVT_REHAB', 
    studentCount: 350,
    subscriptionStatus: 'EXPIRED',
    subscriptionExpiry: '2026-06-01',
    modules: ['aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 'student', 'therapist', 'hospital', 'inventory', 'analytics', 'ai'] 
  }
];

const ALL_16_MODULES = [
  { key: 'aac', label: 'AAC Speech Synthesizer' },
  { key: 'speech-therapy', label: 'Speech & Articulation' },
  { key: 'autism', label: 'Autism & ABA Behavior' },
  { key: 'sign-language', label: 'Sign Language & Gestures' },
  { key: 'braille', label: 'Braille & Tactile Learning' },
  { key: 'student', label: 'Student & IEP Progress' },
  { key: 'therapist', label: 'Therapist Workspace & SOAP' },
  { key: 'teacher', label: 'Teacher & Classroom' },
  { key: 'assessment', label: 'Diagnostic Assessment' },
  { key: 'school-erp', label: 'Special School ERP' },
  { key: 'hospital', label: 'Rehabilitation Hospital' },
  { key: 'billing', label: 'NDIS & Insurance Billing' },
  { key: 'communication', label: 'Parent Communication' },
  { key: 'inventory', label: 'Assistive Device Inventory' },
  { key: 'analytics', label: 'Executive Analytics BI' },
  { key: 'ai', label: 'AI Copilot Summarizer' }
];

export const SuperAdminDashboard: React.FC = () => {
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdmin[]>(INITIAL_TENANT_ADMINS);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);

  // New Admin Form State
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [plan, setPlan] = useState('ENTERPRISE_SCHOOL');
  const [selectedModules, setSelectedModules] = useState<string[]>(['aac', 'student', 'therapist', 'ai']);
  const [successMsg, setSuccessMsg] = useState('');

  const toggleModuleForAdmin = (adminId: string, moduleKey: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const exists = item.modules.includes(moduleKey);
        const updated = exists
          ? item.modules.filter((m) => m !== moduleKey)
          : [...item.modules, moduleKey];
        return { ...item, modules: updated };
      })
    );
  };

  const toggleSubscriptionStatus = (adminId: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const newStatus = item.subscriptionStatus === 'ACTIVE' ? 'EXPIRED' : 'ACTIVE';
        return { 
          ...item, 
          subscriptionStatus: newStatus,
          subscriptionExpiry: newStatus === 'ACTIVE' ? '2027-08-31' : 'Expired (Action Required)'
        };
      })
    );
    setSuccessMsg('Updated tenant subscription status successfully!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleCreateTenantAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminName || !adminEmail || !schoolName) return;

    const slug = schoolName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const newAdmin: TenantAdmin = {
      id: Date.now().toString(),
      adminName,
      adminEmail,
      schoolName,
      slug,
      plan,
      studentCount: 0,
      subscriptionStatus: 'ACTIVE',
      subscriptionExpiry: '2027-08-31',
      modules: selectedModules
    };

    setTenantAdmins([newAdmin, ...tenantAdmins]);
    setSuccessMsg(`Tenant Admin "${adminName}" created & active subscription granted for ${schoolName}!`);
    setAdminName('');
    setAdminEmail('');
    setSchoolName('');
    setIsAddAdminOpen(false);

    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full uppercase tracking-wider mb-2">
            <Shield className="w-3.5 h-3.5" /> Super Admin Control Plane
          </div>
          <h2 className="text-2xl font-black text-slate-900">Tenant Admin & Subscription Licensing Matrix</h2>
          <p className="text-xs text-slate-500">Grant active subscriptions, add school administrators, and toggle module authorizations in real-time</p>
        </div>

        <button
          onClick={() => setIsAddAdminOpen(true)}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow-md transition"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add New Tenant Admin</span>
        </button>
      </div>

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
          <Check className="w-4 h-4 text-emerald-600" />
          {successMsg}
        </div>
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><Building className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">{tenantAdmins.length}</div>
            <div className="text-xs text-slate-500 font-semibold">Active Tenant Admins</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl"><Zap className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">
              {tenantAdmins.filter(t => t.subscriptionStatus === 'ACTIVE').length}
            </div>
            <div className="text-xs text-slate-500 font-semibold">Active Subscriptions</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl"><Cpu className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">16 / 16</div>
            <div className="text-xs text-slate-500 font-semibold">Authorized Modules</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl"><HardDrive className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">
              {tenantAdmins.reduce((acc, t) => acc + t.studentCount, 0)}
            </div>
            <div className="text-xs text-slate-500 font-semibold">Enrolled Students</div>
          </div>
        </div>
      </div>

      {/* Tenant Admins & Subscriptions Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-6">
        <h3 className="text-lg font-extrabold text-slate-900">Tenant Administrator Accounts & Subscription Licensing</h3>

        <div className="space-y-4">
          {tenantAdmins.map((tenant) => (
            <div key={tenant.id} className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50 space-y-4">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-extrabold text-slate-900 text-base">{tenant.schoolName}</h4>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                      subdomain: {tenant.slug}.abilityos.com
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Admin: <span className="font-bold text-slate-700">{tenant.adminName}</span> ({tenant.adminEmail}) • Plan: <span className="font-bold text-blue-600">{tenant.plan}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Subscription Badge */}
                  <div className="flex items-center gap-1.5">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                      tenant.subscriptionStatus === 'ACTIVE' 
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' 
                        : 'bg-red-100 text-red-800 border border-red-300'
                    }`}>
                      {tenant.subscriptionStatus === 'ACTIVE' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
                      Subscription {tenant.subscriptionStatus}
                    </span>

                    <button
                      onClick={() => toggleSubscriptionStatus(tenant.id)}
                      className="px-3 py-1 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg text-xs font-bold text-slate-700 shadow-sm transition"
                    >
                      {tenant.subscriptionStatus === 'ACTIVE' ? 'Revoke Subscription' : 'Grant Valid Subscription'}
                    </button>
                  </div>
                </div>
              </div>

              {/* 16 Module Authorizations for this Tenant */}
              <div>
                <div className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider mb-2">
                  Enabled Modules ({tenant.modules.length} of 16 Authorized):
                </div>
                
                <div className="flex flex-wrap gap-1.5">
                  {ALL_16_MODULES.map((m) => {
                    const isEnabled = tenant.modules.includes(m.key);
                    return (
                      <button
                        key={m.key}
                        onClick={() => toggleModuleForAdmin(tenant.id, m.key)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                          isEnabled 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'bg-white text-slate-400 border border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {isEnabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 text-slate-300" />}
                        <span>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* Add Tenant Admin Modal */}
      {isAddAdminOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-blue-900 text-white p-6 flex justify-between items-center">
              <h3 className="text-lg font-bold">Add New Tenant Administrator</h3>
              <button onClick={() => setIsAddAdminOpen(false)} className="text-blue-200 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleCreateTenantAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">School / Clinic Name</label>
                <input
                  type="text" required
                  value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g. St. Xavier Rehabilitation School"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Admin Full Name</label>
                  <input
                    type="text" required
                    value={adminName} onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Dr. Sarah Connor"
                    className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Admin Email</label>
                  <input
                    type="email" required
                    value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="sarah@stxavier.edu"
                    className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subscription Plan</label>
                <select
                  value={plan} onChange={(e) => setPlan(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ENTERPRISE_SCHOOL">Enterprise Special School Plan ($499/mo)</option>
                  <option value="PRO_CLINIC">Pro Rehabilitation Clinic ($299/mo)</option>
                  <option value="GOVT_REHAB">Government Rehab Department ($999/mo)</option>
                </select>
              </div>

              <div className="pt-3 border-t flex justify-end gap-3">
                <button type="button" onClick={() => setIsAddAdminOpen(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow">Provision Admin & Activate Subscription</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
